/**
 * React dependencies
 */

import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';

/**
 * Carousel dependencies
 */

import { Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

/**
 * DWT Dependencies
 */
import { arrowLeftCarouselIcon, arrowRightCarouselIcon, arrowRightIcon } from '.././img/icons';

/**
 * DWT components
 */

import Book from './Books/Book';
import Contributor from './Contributors/Contributor';
import Loader from './Loader';

/**
 * Utility functions
 */
import { removeTrapFocus, getFocusableElements } from './utils/component-utils';

/**
 * Carousel
 *
 * A DWT reusable component
 *
 * @param {Object}  props
 * @param {Array}   props.data
 * @param {Object}  props.context
 * @param {boolean} props.showMetadata
 * @param {boolean} props.error
 */

const Carousel = React.memo( ({ data, context, showMetadata, error }) => {
  const [ delay, setDelay ] = useState( true );
  const [ swiperProgress, setSwiperProgress ] = useState( 0 );
  const [ locked, setLocked ] = useState( false );
  const [ lockedClassName, setLockedClassName ] = useState( '' );
  const swiperRef = useRef();
  const carouselContainerRef = useRef();
  const lastActiveIndexRef = useRef( 0 );
  const filtersEnabled = document.querySelector( '#carousel.team-member-grid-filters-enabled' ) !== null;

  const ctaSlideData = useMemo( () => context?.cta ? JSON.parse( context.cta ) : null, [ context?.cta ] );
  const block = context.featureBy === 'books' ? 'wp-block-dwt-book block__book' : 'wp-block-dwt-contributor block__contributor';

  // Use useCallback to memoize the function
  const handleNextSlide = useCallback( () => {
    swiperRef.current?.slideNext();
  }, [ swiperRef ] );
  const handlePrevSlide = useCallback( () => {
    swiperRef.current?.slidePrev();
  }, [ swiperRef ] );

  const checkLockedStatus = ( swiper ) => {
    return swiper.isLocked ? setLockedClassName( 'is-locked flex justify-center' ) : setLockedClassName( '' );
  };

  /**
   * Helper function to calculate current slides per view based on breakpoints
   * This consolidates repeated logic that appears in multiple places
   */
  const calculateSlidesPerView = useCallback( () => {
    if ( !swiperRef.current ) return 1;
    const swiper = swiperRef.current;
    
    let slidesPerView = swiper.params.slidesPerView;
    if ( typeof slidesPerView === 'string' && slidesPerView === 'auto' ) { 
      slidesPerView = 1;
    }
    
    if ( swiper.params.breakpoints ) {
      const width = window.innerWidth;
      Object.entries( swiper.params.breakpoints ).forEach( ( [ bp, opts ] ) => {
        if ( width >= parseInt( bp ) && opts.slidesPerView ) {
          slidesPerView = opts.slidesPerView;
        }
      });
    }
    
    return slidesPerView;
  }, [] );

  /**
   * Helper to determine if we're in a single-slide view (small mobile)
   */
  const isSingleSlideView = useCallback( () => {
    const slidesPerView = calculateSlidesPerView();
    // For small screens or when 1.25 slides are shown, treat as single slide
    return slidesPerView === 1 || slidesPerView === 1.25;
  }, [ calculateSlidesPerView ] );

  // Helper: get all focusable elements in a given slide index
  const getSlideFocusableEls = ( slideIndex ) => {
    if ( !swiperRef.current ) return [];
    const swiper = swiperRef.current;
    const slide = swiper.slides[slideIndex];
    if ( !slide ) return [];
    return getFocusableElements( slide );
  };

  // Helper: get all focusable elements in all currently visible slides
  const getVisibleSlidesFocusableEls = useCallback( () => {
    if ( !swiperRef.current ) return [];
    const swiper = swiperRef.current;
    
    const slidesPerView = calculateSlidesPerView();
    const start = swiper.activeIndex;
    const end = Math.min( start + slidesPerView, swiper.slides.length );
    
    let focusEls = [];
    for ( let i = start; i < end; i++ ) {
      focusEls = focusEls.concat( getFocusableElements( swiper.slides[i] ) );
    }
    return focusEls;
  }, [ calculateSlidesPerView ] );

  // Helper: move focus to first focusable in a specific slide
  const focusFirstInSlide = ( slideIndex ) => {
    const els = getSlideFocusableEls( slideIndex );
    if ( els.length ) els[0].focus();
  };

  // Helper: move focus to first focusable in the first newly revealed slide
  const focusFirstInNewlyRevealedSlide = useCallback( ( direction ) => {
    if ( !swiperRef.current ) return;
    const swiper = swiperRef.current;
    const slidesPerView = calculateSlidesPerView();
    const currentActiveIndex = swiper.activeIndex;
    const isSmallScreen = isSingleSlideView();
    
    if ( direction === 'next' ) {
      // For "next", focus on the newly revealed slide
      if ( isSmallScreen ) {
        // On small screens, the newly revealed slide is simply the current active slide
        focusFirstInSlide( currentActiveIndex );
      } else {
        // On larger screens, it's the last visible one
        const newlyRevealedIndex = Math.min( currentActiveIndex + slidesPerView - 1, swiper.slides.length - 1 );
        focusFirstInSlide( newlyRevealedIndex );
      }
    } else if ( direction === 'prev' ) {
      // For "prev", focus on the first visible slide which is the newly revealed one
      focusFirstInSlide( currentActiveIndex );
    }
    
    // Update last active index for next navigation
    lastActiveIndexRef.current = currentActiveIndex;
  }, [ calculateSlidesPerView, isSingleSlideView ] );

  const handleArrowKeyDown = useCallback( ( direction ) => ( e ) => {
    if ( e.key === 'Enter' || e.key === ' ' ) {
      e.preventDefault();
      if ( !swiperRef.current ) return;
      
      // Store current active index before navigation
      lastActiveIndexRef.current = swiperRef.current.activeIndex;
      
      if ( direction === 'next' && !swiperRef.current.isEnd ) {
        swiperRef.current.slideNext();
        setTimeout( () => focusFirstInNewlyRevealedSlide( 'next' ), 100 );
      }
      if ( direction === 'prev' && !swiperRef.current.isBeginning ) {
        swiperRef.current.slidePrev();
        setTimeout( () => focusFirstInNewlyRevealedSlide( 'prev' ), 100 );
      }
    }
  }, [ focusFirstInNewlyRevealedSlide ] );

  // Helper functions to determine if navigation buttons are visible/enabled
  const areNavigationButtonsVisible = useCallback( () => {
    if ( !swiperRef.current ) return false;
    
    // On mobile (or when buttons are hidden), we consider them not visible
    const prevBtn = carouselContainerRef.current?.querySelector( '.swiper-button-prev' );
    const nextBtn = carouselContainerRef.current?.querySelector( '.swiper-button-next' );
    
    // Check if buttons are present and visible (not display:none)
    const isPrevVisible = prevBtn && window.getComputedStyle( prevBtn ).display !== 'none';
    const isNextVisible = nextBtn && window.getComputedStyle( nextBtn ).display !== 'none';
    
    return isPrevVisible || isNextVisible;
  }, [] );
  
  const canNavigateNext = useCallback( () => {
    if ( !swiperRef.current ) return false;
    return !swiperRef.current.isEnd;
  }, [] );
  
  const canNavigatePrev = useCallback( () => {
    if ( !swiperRef.current ) return false;
    return !swiperRef.current.isBeginning;
  }, [] );

  // Enhanced helper to determine visible slide range
  const getVisibleSlideRange = useCallback( () => {
    if ( !swiperRef.current ) return { start: 0, end: 0, slidesPerView: 1 };
    const swiper = swiperRef.current;
    
    const slidesPerView = calculateSlidesPerView();
    const start = swiper.activeIndex;
    const end = Math.min( start + slidesPerView, swiper.slides.length );
    
    return { start, end, slidesPerView };
  }, [ calculateSlidesPerView ] );

  // Initial loading delay effect
  useEffect( () => {
    const handleDelay = setTimeout( () => {
      setDelay( false );
    }, 2000 );

    return () => clearTimeout( handleDelay ); // Using clearTimeout instead of clearInterval
  }, [] );

  // Keyboard navigation effect
  useEffect( () => {
    const container = carouselContainerRef.current;
    if ( !container ) return;

    const handleKeyDown = ( e ) => {
      if ( e.key !== 'Tab' ) return;
      const swiper = swiperRef.current;
      if ( !swiper ) return;

      // DOM elements and state
      const prevBtn = container.querySelector( '.swiper-button-prev' );
      const nextBtn = container.querySelector( '.swiper-button-next' );
      const prevDisabled = prevBtn?.classList.contains( 'swiper-button-disabled' );
      const nextDisabled = nextBtn?.classList.contains( 'swiper-button-disabled' );
      
      // Get navigation state
      const slideEls = getVisibleSlidesFocusableEls();
      const navButtonsVisible = areNavigationButtonsVisible();
      const isSmallScreen = isSingleSlideView();
      
      // Build focus order array
      let focusOrder = [ ...slideEls ];
      
      // Only include prev/next buttons if they're visible and not disabled
      if ( navButtonsVisible ) {
        if ( nextBtn && !nextDisabled ) focusOrder.push( nextBtn );
        if ( prevBtn && !prevDisabled ) focusOrder.unshift( prevBtn );
      }

      const currentIndex = focusOrder.indexOf( document.activeElement );
      
      // KEYBOARD NAVIGATION HANDLING
      
      // When shift+tabbing backwards
      if ( e.shiftKey ) {
        // If at the first focusable element
        if ( currentIndex === 0 ) {
          // If nav buttons are visible but we're at the first element
          if ( navButtonsVisible ) {
            // Standard behavior: release focus trap if we're at the very beginning
            removeTrapFocus( container );
          } else if ( canNavigatePrev() ) {
            // On mobile (no visible nav buttons) and we can go to previous slides
            e.preventDefault();
            
            // Store the current active index before navigation
            lastActiveIndexRef.current = swiper.activeIndex;
            
            // Navigate to previous slide
            swiper.slidePrev();
            
            // After slide transition, focus on the last focusable element in the new visible area
            setTimeout( () => {
              const newVisibleEls = getVisibleSlidesFocusableEls();
              if ( newVisibleEls.length ) {
                newVisibleEls[newVisibleEls.length - 1].focus();
              }
            }, 100 );
          }
          // If we can't navigate back, let the browser handle tab navigation
        } else if ( currentIndex > 0 ) {
          // Normal backward tabbing within the carousel
          e.preventDefault();
          focusOrder[currentIndex - 1].focus();
        }
      } else {
        // When tabbing forward
        // If at the last focusable element
        if ( currentIndex === focusOrder.length - 1 ) {
          // If nav buttons are visible but we're at the last element
          if ( navButtonsVisible ) {
            // Standard behavior: release focus trap if we're at the very end
            removeTrapFocus( container );
          } else if ( canNavigateNext() ) {
            // On mobile (no visible nav buttons) and we can go to next slides
            e.preventDefault();
            
            // Navigate to next slide
            swiper.slideNext();
            
            // Store current active index before navigation
            lastActiveIndexRef.current = swiper.activeIndex;
            
            // After slide transition, focus on the first focusable element in the newly revealed slide
            setTimeout( () => {
              if ( isSmallScreen ) {
                // On small screens where only one slide is visible, focus on the first element of the current slide
                focusFirstInSlide( swiper.activeIndex );
              } else {
                // On larger screens, calculate which slide is newly revealed (should be the last visible one)
                const { slidesPerView } = getVisibleSlideRange();
                const newlyRevealedIndex = Math.min( swiper.activeIndex + slidesPerView - 1, swiper.slides.length - 1 );
                
                // Focus on the first element in that specific slide
                focusFirstInSlide( newlyRevealedIndex );
              }
            }, 100 );
          }
          // If we can't navigate forward, let the browser handle tab navigation
        } else if ( currentIndex > -1 ) {
          // Normal forward tabbing within the carousel
          e.preventDefault();
          focusOrder[currentIndex + 1].focus();
        }
      }
    };

    container.addEventListener( 'keydown', handleKeyDown );
    return () => {
      container.removeEventListener( 'keydown', handleKeyDown );
    };
  }, [
    getVisibleSlideRange,
    getVisibleSlidesFocusableEls,
    areNavigationButtonsVisible,
    canNavigateNext,
    canNavigatePrev,
    isSingleSlideView
  ] );

  return (
    <div ref={carouselContainerRef}>
      <button
        className={ `swiper-button-prev ${swiperProgress === 0 ? 'swiper-button-disabled' : ''}` }
        type='button'
        onClick={ handlePrevSlide }
        onKeyDown={handleArrowKeyDown( 'prev' )}
        aria-label='Previous'
      >
        { arrowLeftCarouselIcon }
      </button>
      
      {/* Swiper component */}
      <Swiper
        breakpoints={ {
          0: {
            slidesPerView: 'auto'
          },
          480: {
            slidesPerView: 2
          },
          768: {
            slidesPerView: 3
          },
          1024: {
            slidesPerView: filtersEnabled ? 2 : 4
          }
        } }
        className={ lockedClassName }
        modules={ [ Navigation ] }
        onBeforeInit={ ( swiper ) => {
          swiperRef.current = swiper;
        } }
        onResize={ ( swiper ) => {
          setLocked( swiper.isLocked );
          checkLockedStatus( swiper );
        } }
        onSlideChange={ ( swiper ) => {
          setSwiperProgress( swiper.progress );
        } }
        resizeObserver
        slidesPerView={ 1.25 }
        spaceBetween={ 20 }
        watchOverflow
        watchSlidesProgress
      >
        {/* Render slides */}
        { !error && data.length > 0 && data?.map( ( item, index ) => (
          <SwiperSlide className={ `wp-block-dwt-book block__book relative ${block}` } data-list-index={ index } key={ index }>

            { delay && (
              <Loader showMetadata={ showMetadata } />
            ) }

            { !delay && context.featureBy === 'books' && (
              <Book
                id={ index }
                item={ item }
                showMetadata={ showMetadata }
              />
            ) }

            { !delay && context.featureBy === 'contributors' && (
              <Contributor
                item={ item }
                showMetadata={ showMetadata }
              />
            ) }

          </SwiperSlide>
        ) ) }

        {/* CTA slide if provided */}
        { ctaSlideData && ctaSlideData.text && (
          <SwiperSlide className="w-full h-full flex justify-center">
            <div className="cta-wrapper w-full">
              { ctaSlideData?.href && (
                <a className="is-text-button no-underline" href={ ctaSlideData.href } target={ ctaSlideData.target ? '_blank' : null } rel={ ctaSlideData.target ? 'noopener noreferrer' : null }>{ ctaSlideData?.text }</a>
              ) }

              { !ctaSlideData?.href && (
                <button className="is-text-button" type="button">
                  { ctaSlideData?.text }
                </button>
              ) }

              { arrowRightIcon }
            </div>
          </SwiperSlide>
        ) }
      </Swiper>
      
      <button
        className={ `swiper-button-next ${swiperProgress === 1 || locked === true ? 'swiper-button-disabled' : ''} ${delay ? 'swiper-button-delay' : ''}` }
        type='button'
        onClick={ handleNextSlide }
        onKeyDown={handleArrowKeyDown( 'next' )}
        aria-label='Next'
      >
        { arrowRightCarouselIcon }
      </button>
    </div>
  );
});

export default Carousel;
